import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { usePathname } from 'src/routes/hooks';

import { getConfig } from 'src/global-config';
import { useOrganisation } from 'src/organisation';

import { useAuthContext } from 'src/auth/hooks/use-auth-context';

import { notifyProductFruitsPageChanged } from './product-fruits-utils';

/**
 * Product Fruits init - calls $productFruits.push(['init', ...]) when user is authenticated.
 * Includes customNavigation for SPA routing and pageChanged on route changes so tours show correctly.
 * Script loader is in index.html.
 */
export function ProductFruitsInit() {
  const { user, authenticated } = useAuthContext();
  const organisation = useOrganisation();
  const navigate = useNavigate();
  const pathname = usePathname();
  const initializedRef = useRef(false);
  const lastUserInfoRef = useRef<string | null>(null);

  useEffect(() => {
    if (!authenticated) {
      initializedRef.current = false;
      lastUserInfoRef.current = null;
      return;
    }

    const CONFIG = getConfig();
    const enabled = CONFIG.productFruits?.enabled ?? false;
    const workspaceCode = CONFIG.productFruits?.workspaceCode ?? '';

    if (!enabled || !workspaceCode || !user) {
      return;
    }

    // Prefer organisation (Profile Settings) over Auth0 for name/email. Init without org so chat works
    // when org fetch is slow/fails (e.g. local dev). PF requires non-empty username.
    const rawUsername =
      organisation?.email || user?.email || user?.sub || user?.id;
    const username =
      (typeof rawUsername === 'string' ? rawUsername.trim() : String(rawUsername ?? '')) ||
      `user-${Date.now()}`;
    if (!username) return;

    const userInfo = {
      username,
      email: organisation?.email ?? user?.email ?? '',
      firstname: organisation?.firstName ?? user?.given_name ?? user?.firstName ?? '',
      lastname: organisation?.lastName ?? user?.family_name ?? user?.lastName ?? '',
      role: user.role ?? 'admin',
      props: {
        organizationSlug: organisation?.organizationSlug ?? '',
        organizationId: organisation?.organizationId ?? '',
      },
    };
    const userInfoString = JSON.stringify(userInfo);

    // Allow re-init when effective user info changes (e.g., organisation loads later),
    // but skip if we've already initialized with identical data.
    if (initializedRef.current && lastUserInfoRef.current === userInfoString) {
      return;
    }

    const initOptions = {
      disableLocationChangeDetection: true,
      customNavigation: {
        use: true,
        navigate: (url: string, openInNewTab?: boolean) => {
          if (/^https?:\/\//.test(url)) {
            if (openInNewTab) {
              window.open(url, '_blank', 'noopener,noreferrer');
            } else {
              window.location.href = url;
            }
          } else if (openInNewTab) {
            window.open(url, '_blank');
          } else {
            navigate(url);
          }
        },
        onGet: () => window.location.href,
      },
    };

    (window as any).$productFruits?.push?.([
      'init',
      workspaceCode,
      'en',
      userInfo,
      initOptions,
    ]);
    initializedRef.current = true;
    lastUserInfoRef.current = userInfoString;
  }, [authenticated, user, organisation, navigate]);

  useEffect(() => {
    if (!authenticated) return;

    const CONFIG = getConfig();
    const enabled = CONFIG.productFruits?.enabled ?? false;
    if (!enabled) return;

    notifyProductFruitsPageChanged();
  }, [pathname, authenticated]);

  return null;
}
